import type { Dispatch, SetStateAction } from 'react';
import { Button, FormGroup, FormSection } from '@ksyos/design-system';
import { difference } from 'lodash';
import { useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { IEmbloomQuestionnairesFormSectionReadProps } from './EmbloomQuestionnairesFormSectionRead';
import { translateKey } from '../../../../../translate';
import { InputCheckbox } from '../../../../elements/common';
import { ActionBarShowAndRemoveSelection } from '../../../../userDataManagement/ActionBarShowAndRemoveSelection';
import { AddEmbloomQuestionnaireModal } from './AddEmbloomQuestionnaireModal';

interface IEmbloomQuestionnairesFormSectionEditProps extends IEmbloomQuestionnairesFormSectionReadProps {
    embloomQuestionnaires: { embloomQuestionnaire: IEmbloomQuestionnaire }[];
    loadStandardEmbloomQuestionnaires: () => Promise<void>;
    onSave: (questionnaires: IEmbloomQuestionnaire[]) => Promise<void>;
    setEmbloomQuestionnaires: Dispatch<SetStateAction<{ embloomQuestionnaire: IEmbloomQuestionnaire }[]>>;
    setShowAddEmbloomQuestionnaireModal: Dispatch<SetStateAction<boolean>>;
    showAddEmbloomQuestionnaireModal: boolean;
}

export function EmbloomQuestionnairesFormSectionEdit(props: IEmbloomQuestionnairesFormSectionEditProps) {
    const [selectedEmbloomQuestionnaires, setSelectedEmbloomQuestionnaires] = useState<
        { embloomQuestionnaire: IEmbloomQuestionnaire }[]
    >([]);

    return (
        <FormSection>
            <Button
                onClick={async () => await props.loadStandardEmbloomQuestionnaires()}
                sx={{ mb: 4 }}
                variant="link"
            >
                {translateKey('embloomQuestionnaires.standardQuestionnaires')}
            </Button>

            {props.decisionSupportEmbloomQuestionnaires.length > 0 && (
                <FormGroup label={translateKey('embloomQuestionnaires.isDecisionSupport')}>
                    {props.decisionSupportEmbloomQuestionnaires.map((questionnaire, key) => (
                        <div
                            key={key}
                            style={{ display: 'inline' }}
                        >
                            <InputCheckbox
                                checked={
                                    !!selectedEmbloomQuestionnaires.find(
                                        (selectedEmbloomQuestionnaire) =>
                                            selectedEmbloomQuestionnaire.embloomQuestionnaire.id ===
                                            questionnaire.embloomQuestionnaire.id
                                    )
                                }
                                onChange={() => handleCheckUncheckQuestionnaire(questionnaire)}
                                text={questionnaire.embloomQuestionnaire.label}
                            />
                        </div>
                    ))}
                </FormGroup>
            )}

            {props.nonDecisionSupportEmbloomQuestionnaires.length > 0 && (
                <FormGroup label={translateKey('embloomQuestionnaires.otherQuestionnaires')}>
                    {props.nonDecisionSupportEmbloomQuestionnaires.map((questionnaire, key) => (
                        <InputCheckbox
                            checked={
                                !!selectedEmbloomQuestionnaires.find(
                                    (selectedEmbloomQuestionnaire) =>
                                        selectedEmbloomQuestionnaire.embloomQuestionnaire.id ===
                                        questionnaire.embloomQuestionnaire.id
                                )
                            }
                            key={key}
                            onChange={() => handleCheckUncheckQuestionnaire(questionnaire)}
                            text={questionnaire.embloomQuestionnaire.label}
                        />
                    ))}
                    <ActionBarShowAndRemoveSelection
                        handleClose={handleCloseActionBar}
                        handleDelete={handleDeleteSelectedQuestionnaires}
                        isOpen={selectedEmbloomQuestionnaires.length > 0}
                        selectedIds={selectedEmbloomQuestionnaires.map(
                            (selectedEmbloomQuestionnaire) => selectedEmbloomQuestionnaire.embloomQuestionnaire.id
                        )}
                    />
                </FormGroup>
            )}
            <Button
                iconLeft="add"
                onClick={() => props.setShowAddEmbloomQuestionnaireModal(true)}
                sx={{ mb: 4 }}
                variant="link"
            >
                {translateKey('embloomQuestionnaires.addQuestionnaire')}
            </Button>

            {props.showAddEmbloomQuestionnaireModal && (
                <AddEmbloomQuestionnaireModal
                    checkedQuestionnaires={props.embloomQuestionnaires}
                    onCloseModal={() => props.setShowAddEmbloomQuestionnaireModal(false)}
                    onSave={async (questionnaires) => props.onSave(questionnaires)}
                />
            )}
        </FormSection>
    );

    function handleCheckUncheckQuestionnaire(questionnaire: { embloomQuestionnaire: IEmbloomQuestionnaire }) {
        if (!selectedEmbloomQuestionnaires.includes(questionnaire)) {
            return setSelectedEmbloomQuestionnaires([...selectedEmbloomQuestionnaires, questionnaire]);
        } else {
            return setSelectedEmbloomQuestionnaires(
                selectedEmbloomQuestionnaires.filter(
                    (selectedEmbloomQuestionnaire) => selectedEmbloomQuestionnaire !== questionnaire
                )
            );
        }
    }

    async function handleCloseActionBar() {
        setSelectedEmbloomQuestionnaires([]);
    }

    async function handleDeleteSelectedQuestionnaires() {
        const newList = difference(props.embloomQuestionnaires, selectedEmbloomQuestionnaires);
        setSelectedEmbloomQuestionnaires([]);
        props.setEmbloomQuestionnaires(newList);
    }
}
